import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import UserManagement from './components/UserManagement';
import DepositValidation from './components/DepositValidation';
import WithdrawalValidation from './components/WithdrawalValidation';
import Promoteurs from './components/Promoteurs';
import SiteSettings from './components/SiteSettings';
import AdminManagement from './components/AdminManagement';
import ServiceClientAdmin from './components/ServiceClientAdmin';
import { syncUsersFromRemote, getAllActivationRequests, siteKey } from '../../utils/sharedStorage';
import { cleanAllForeignData, shouldRunAutoCleanup, markAutoCleanupDone } from '../../utils/dataCleanup';

interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  pendingUsers: number;
  pendingDeposits: number;
  pendingWithdrawals: number;
  newUsersToday: number;
  pendingMessages: number;
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0, activeUsers: 0, pendingUsers: 0,
    pendingDeposits: 0, pendingWithdrawals: 0, newUsersToday: 0, pendingMessages: 0,
  });
  const [newUserAlert, setNewUserAlert] = useState(false);
  const [cleanupAlert, setCleanupAlert] = useState<{ users: number; requests: number } | null>(null);
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const prevTotalRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cleanupDoneRef = useRef(false);

  useEffect(() => {
    const adminSession = sessionStorage.getItem('adminSession');
    if (!adminSession) { navigate('/connexion'); return; }
    try {
      const session = JSON.parse(adminSession);
      if (!session.isAdmin || !session.phone) {
        navigate('/connexion');
      } else {
        sessionStorage.setItem('adminPhone', session.phone);
        // ── Nettoyage automatique au démarrage (une seule fois par jour) ──
        if (!cleanupDoneRef.current && shouldRunAutoCleanup()) {
          cleanupDoneRef.current = true;
          cleanAllForeignData().then(result => {
            if (result.totalDeleted > 0) {
              setCleanupAlert({
                users: result.deletedUsers,
                requests: result.deletedActivationRequests + result.deletedWithdrawalRequests
              });
              setTimeout(() => setCleanupAlert(null), 8000);
              markAutoCleanupDone();
            }
          });
        }
      }
    } catch { navigate('/connexion'); }
  }, [navigate]);

  useEffect(() => {
    loadStats();
    // ── Polling toutes les 1 seconde ──
    intervalRef.current = setInterval(loadStats, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const loadStats = async () => {
    try {
      const users = await syncUsersFromRemote();
      const requests = await getAllActivationRequests();

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const newToday = users.filter(u => new Date(u.registrationDate) >= today).length;

      const rawW = localStorage.getItem(siteKey('withdrawalRequests')) || '[]';
      let withdrawals: any[] = [];
      try { withdrawals = JSON.parse(rawW); } catch { withdrawals = []; }
      const pendingW = withdrawals.filter((w: any) => w.status === 'pending').length;

      let pendingMsgs = 0;
      try {
        const msgs = JSON.parse(localStorage.getItem('serviceClientMessages') || '[]');
        pendingMsgs = Array.isArray(msgs) ? msgs.filter((m: any) => m.status === 'pending').length : 0;
      } catch { pendingMsgs = 0; }

      const newStats: AdminStats = {
        totalUsers: users.length,
        activeUsers: users.filter(u => u.isActivated && u.accountStatus === 'active').length,
        pendingUsers: users.filter(u => !u.isActivated || u.accountStatus === 'pending').length,
        pendingDeposits: requests.filter(r => r.status === 'pending').length,
        pendingWithdrawals: pendingW,
        newUsersToday: newToday,
        pendingMessages: pendingMsgs,
      };

      if (prevTotalRef.current > 0 && users.length > prevTotalRef.current) {
        setNewUserAlert(true);
        setTimeout(() => setNewUserAlert(false), 6000);
      }
      prevTotalRef.current = users.length;

      setStats(newStats);
    } catch (e) {
      console.error('Erreur chargement stats admin:', e);
    }
  };

  const handleManualCleanup = async () => {
    setIsCleaningUp(true);
    const result = await cleanAllForeignData();
    setIsCleaningUp(false);
    setShowCleanupConfirm(false);
    
    if (result.totalDeleted > 0) {
      setCleanupAlert({
        users: result.deletedUsers,
        requests: result.deletedActivationRequests + result.deletedWithdrawalRequests
      });
      setTimeout(() => setCleanupAlert(null), 8000);
      loadStats(); // Recharger les stats
    }
  };

  const tabs = [
    { id: 'users',       label: 'Utilisateurs',  icon: 'ri-user-line',                  badge: stats.pendingUsers },
    { id: 'deposits',    label: 'Dépôts',         icon: 'ri-money-dollar-circle-line',   badge: stats.pendingDeposits },
    { id: 'withdrawals', label: 'Retraits',        icon: 'ri-bank-card-line',             badge: stats.pendingWithdrawals },
    { id: 'promoters',   label: 'Promoteurs',      icon: 'ri-team-line',                  badge: 0 },
    { id: 'service',     label: 'Service Client',  icon: 'ri-customer-service-2-line',    badge: stats.pendingMessages },
    { id: 'settings',    label: 'Paramètres',      icon: 'ri-settings-3-line',            badge: 0 },
    { id: 'admins',      label: 'Admins',          icon: 'ri-shield-user-line',           badge: 0 },
  ];

  const statCards = [
    { label: 'Total membres', value: stats.totalUsers, icon: 'ri-group-line', sub: `+${stats.newUsersToday} auj.` },
    { label: 'Actifs', value: stats.activeUsers, icon: 'ri-checkbox-circle-line', sub: `${stats.totalUsers > 0 ? Math.round(stats.activeUsers / stats.totalUsers * 100) : 0}%` },
    { label: 'En attente', value: stats.pendingUsers, icon: 'ri-time-line', sub: 'Activation' },
    { label: 'Dépôts', value: stats.pendingDeposits, icon: 'ri-money-dollar-circle-line', sub: 'À valider' },
    { label: 'Retraits', value: stats.pendingWithdrawals, icon: 'ri-bank-card-line', sub: 'À traiter' },
    { label: 'Messages', value: stats.pendingMessages, icon: 'ri-message-3-line', sub: 'Sans réponse' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Modale confirmation nettoyage */}
      {showCleanupConfirm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-delete-bin-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Nettoyer les données étrangères</h3>
                <p className="text-sm text-gray-500">Supprimer tous les comptes non-SUCCESSLINK</p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-amber-800 mb-2">
                <strong>⚠️ Cette action va supprimer :</strong>
              </p>
              <ul className="text-sm text-amber-700 space-y-1 ml-4">
                <li>• Tous les comptes Beluga</li>
                <li>• Tous les comptes d'autres sites</li>
                <li>• Leurs demandes de paiement</li>
                <li>• Leurs demandes de retrait</li>
              </ul>
              <p className="text-xs text-amber-600 mt-2">
                Les admins SUCCESSLINK seront préservés.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCleanupConfirm(false)}
                disabled={isCleaningUp}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleManualCleanup}
                disabled={isCleaningUp}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isCleaningUp ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Nettoyage...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line"></i>
                    Nettoyer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alerte nettoyage effectué */}
      {cleanupAlert && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4">
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
              <i className="ri-check-line text-lg"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">✅ Nettoyage terminé</p>
              <p className="text-xs text-green-100">
                {cleanupAlert.users} compte(s) + {cleanupAlert.requests} demande(s) supprimés
              </p>
            </div>
            <button onClick={() => setCleanupAlert(null)} className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full cursor-pointer hover:bg-white/30 flex-shrink-0">
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      )}

      {/* Alerte nouvel utilisateur */}
      {newUserAlert && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4">
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3 animate-bounce">
            <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
              <i className="ri-user-add-line text-lg"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">🎉 Nouvel inscrit !</p>
              <p className="text-xs text-teal-100">Vérifiez l'onglet Utilisateurs</p>
            </div>
            <button onClick={() => setNewUserAlert(false)} className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full cursor-pointer hover:bg-white/30 flex-shrink-0">
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                <i className="ri-shield-star-line text-xl text-white"></i>
              </div>
              <div>
                <h1 className="text-base sm:text-xl font-bold tracking-tight leading-tight">Panel Admin</h1>
                <p className="text-teal-100 text-xs font-medium mt-0.5 hidden sm:block">SUCCESSLINK AGENCY</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Indicateur temps réel */}
              <span className="hidden sm:flex items-center gap-1.5 px-3 py-1 bg-white/15 rounded-full text-xs font-bold border border-white/20">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                EN DIRECT
              </span>
              <button
                onClick={() => setShowCleanupConfirm(true)}
                className="bg-red-500/20 hover:bg-red-500/30 border border-red-300/30 p-2 rounded-xl flex items-center justify-center transition-all cursor-pointer"
                title="Nettoyer les données étrangères"
              >
                <i className="ri-delete-bin-line text-base"></i>
              </button>
              <button
                onClick={loadStats}
                className="bg-white/15 hover:bg-white/25 border border-white/20 p-2 rounded-xl flex items-center justify-center transition-all cursor-pointer"
                title="Actualiser"
              >
                <i className="ri-refresh-line text-base"></i>
              </button>
              <button
                onClick={() => navigate('/tableau-de-bord')}
                className="bg-white/15 hover:bg-white/25 border border-white/20 px-3 py-2 rounded-xl flex items-center gap-1.5 transition-all cursor-pointer whitespace-nowrap text-sm font-semibold"
              >
                <i className="ri-eye-line text-base"></i>
                <span className="hidden sm:inline">Voir le site</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats rapides */}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-4">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {statCards.map((card, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-2.5 text-center hover:bg-white/20 transition cursor-default">
                <div className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-lg mx-auto mb-1">
                  <i className={`${card.icon} text-sm text-white`}></i>
                </div>
                <p className="text-xl font-bold text-white leading-none">{card.value}</p>
                <p className="text-xs text-teal-100 mt-0.5 font-medium leading-tight truncate">{card.label}</p>
                <p className="text-xs text-teal-200 opacity-80 truncate">{card.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Onglets de navigation — scroll horizontal sur mobile */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="flex gap-0 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  relative flex items-center gap-1.5 px-3 sm:px-4 py-3 text-xs sm:text-sm font-semibold
                  whitespace-nowrap border-b-2 transition-all cursor-pointer flex-shrink-0
                  ${activeTab === tab.id
                    ? 'border-teal-500 text-teal-600 bg-teal-50/50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }
                `}
              >
                <i className={`${tab.icon} text-sm sm:text-base`}></i>
                <span className="hidden xs:inline sm:inline">{tab.label}</span>
                {tab.badge > 0 && (
                  <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-white text-xs font-bold px-1 ${
                    tab.id === 'users' ? 'bg-amber-500' : 'bg-red-500'
                  } animate-pulse`}>
                    {tab.badge > 99 ? '99+' : tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        {activeTab === 'users'       && <UserManagement />}
        {activeTab === 'deposits'    && <DepositValidation />}
        {activeTab === 'withdrawals' && <WithdrawalValidation />}
        {activeTab === 'promoters'   && <Promoteurs />}
        {activeTab === 'service'     && <ServiceClientAdmin />}
        {activeTab === 'settings'    && <SiteSettings />}
        {activeTab === 'admins'      && <AdminManagement />}
      </div>
    </div>
  );
}